/**
 * Notification hooks using React Query
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsService } from '@/services/notifications';
import type { Notification } from '@/services/notifications';

// Query keys for cache management
export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

/**
 * Hook to fetch all notifications
 */
export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => notificationsService.list(),
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });
}

/**
 * Hook to fetch unread notification count
 * Polls every 30 seconds UNLESS SSE is connected (WP-RT-2)
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationsService.getUnreadCount(),
    // Disable polling when SSE is connected (WP-RT-2)
    refetchInterval: () => {
      const sseConnected = (window as unknown as { __sseConnected?: boolean }).__sseConnected;
      return sseConnected ? false : 30000;
    },
    staleTime: 10 * 1000, // Consider data fresh for 10 seconds
  });
}

/**
 * Hook to mark a single notification as read
 */
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationKeys.unreadCount() });

      // Snapshot previous values
      const previousList = queryClient.getQueryData(notificationKeys.list());
      const previousCount = queryClient.getQueryData(notificationKeys.unreadCount());

      // Optimistically update the list
      queryClient.setQueryData(
        notificationKeys.list(),
        (old: { notifications: Notification[]; unreadCount: number } | undefined) => {
          if (!old) return old;
          return {
            notifications: old.notifications.map((n) =>
              n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
            ),
            unreadCount: Math.max(0, old.unreadCount - 1),
          };
        }
      );

      // Optimistically update the count
      queryClient.setQueryData(
        notificationKeys.unreadCount(),
        (old: { count: number } | undefined) => {
          if (!old) return old;
          return { count: Math.max(0, old.count - 1) };
        }
      );

      return { previousList, previousCount };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationKeys.unreadCount() });

      // Snapshot previous values
      const previousList = queryClient.getQueryData(notificationKeys.list());
      const previousCount = queryClient.getQueryData(notificationKeys.unreadCount());

      // Optimistically update the list
      queryClient.setQueryData(
        notificationKeys.list(),
        (old: { notifications: Notification[]; unreadCount: number } | undefined) => {
          if (!old) return old;
          const now = new Date().toISOString();
          return {
            notifications: old.notifications.map((n) => ({
              ...n,
              isRead: true,
              readAt: n.readAt ?? now,
            })),
            unreadCount: 0,
          };
        }
      );

      // Optimistically update the count
      queryClient.setQueryData(notificationKeys.unreadCount(), { count: 0 });

      return { previousList, previousCount };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

/**
 * Hook to dismiss a notification
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsService.dismiss(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationKeys.unreadCount() });

      // Snapshot previous values
      const previousList = queryClient.getQueryData(notificationKeys.list());
      const previousCount = queryClient.getQueryData(notificationKeys.unreadCount());

      // Find the notification to check if it was unread
      let wasUnread = false;
      queryClient.setQueryData(
        notificationKeys.list(),
        (old: { notifications: Notification[]; unreadCount: number } | undefined) => {
          if (!old) return old;
          const notification = old.notifications.find((n) => n.id === id);
          wasUnread = notification ? !notification.isRead : false;
          return {
            notifications: old.notifications.filter((n) => n.id !== id),
            unreadCount: wasUnread ? Math.max(0, old.unreadCount - 1) : old.unreadCount,
          };
        }
      );

      // Update the count if needed
      if (wasUnread) {
        queryClient.setQueryData(
          notificationKeys.unreadCount(),
          (old: { count: number } | undefined) => {
            if (!old) return old;
            return { count: Math.max(0, old.count - 1) };
          }
        );
      }

      return { previousList, previousCount };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}
